import { StatusIcon, type StatusIconName } from './StatusIcon';
import styles from './MessageBubble.module.css';

export type MessageChannel = 'WHATSAPP' | 'FCM';
export type MessageDeliveryState = 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

const CHANNELS: Record<MessageChannel, { word: string; icon: StatusIconName }> = {
  WHATSAPP: { word: 'WhatsApp', icon: 'chat' },
  FCM: { word: 'Push', icon: 'notifications' },
};

const STATES: Record<MessageDeliveryState, { word: string; icon: StatusIconName }> = {
  SENT: { word: 'Sent', icon: 'check' },
  DELIVERED: { word: 'Delivered', icon: 'done_all' },
  READ: { word: 'Read', icon: 'done_all' },
  FAILED: { word: 'Failed', icon: 'close' },
};

/** DD Mon YYYY, HH:mm IST (UX principle 4). */
function istTime(iso: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );
  return `${parts.day} ${parts.month} ${parts.year}, ${parts.hour}:${parts.minute} IST`;
}

interface Props {
  /** The message text exactly as the shipper gets it (server-built). */
  text: string;
  channel: MessageChannel;
  /** When it was sent (ISO-8601); null for a preview. */
  at: string | null;
  /** Delivery state; omitted for a preview. Always shown as icon + word, never colour alone. */
  deliveryState?: MessageDeliveryState | null;
  /** Optional line above the text, e.g. the template name or "Preview". */
  title?: string;
}

/**
 * One shipper message as a chat bubble (#294): the shipment's Messages tab and the
 * Record-delay preview use it, so the preview looks exactly like what was sent.
 */
export function MessageBubble({ text, channel, at, deliveryState, title }: Props) {
  const ch = CHANNELS[channel] ?? CHANNELS.WHATSAPP;
  const state = deliveryState ? STATES[deliveryState] : null;
  return (
    <div className={styles.bubble} data-failed={deliveryState === 'FAILED' ? 'true' : undefined}>
      {title && <div className={styles.title}>{title}</div>}
      <p className={styles.text}>{text}</p>
      <div className={styles.meta}>
        <span className={styles.channel}>
          <StatusIcon name={ch.icon} size={14} />
          {ch.word}
        </span>
        {at && <span className={styles.time}>{istTime(at)}</span>}
        {state && deliveryState && (
          <span className={styles.state} data-state={deliveryState}>
            <StatusIcon name={state.icon} size={14} />
            {state.word}
          </span>
        )}
      </div>
    </div>
  );
}
